import { useEffect, useState, useCallback } from 'react'
import { apiClient } from '@/lib/api-client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/use-toast'

interface BetaSignup {
  id: string
  email: string
  name: string
  company: string
  githubUsername: string
  useCase?: string
  status: string
  approvedBy?: string
  notes?: string
  submittedAt: string
  reviewedAt?: string
}

export default function AdminBetaSignupsPage() {
  const { toast } = useToast()
  const [signups, setSignups] = useState<BetaSignup[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('pending')
  const [reviewDialog, setReviewDialog] = useState<{ id: string; action: 'approve' | 'decline' } | null>(null)
  const [reviewNotes, setReviewNotes] = useState('')

  const loadSignups = useCallback(async () => {
    setLoading(true)
    const resp = await apiClient.listBetaSignups({ status: statusFilter, limit: 50 })
    if (resp.success && resp.data) {
      const data = resp.data as { betaSignups: BetaSignup[]; total: number }
      setSignups(data.betaSignups || [])
      setTotal(data.total || 0)
    }
    setLoading(false)
  }, [statusFilter])

  useEffect(() => { loadSignups() }, [loadSignups])

  const handleReview = async () => {
    if (!reviewDialog) return
    const { id, action } = reviewDialog
    const resp = action === 'approve'
      ? await apiClient.approveBetaSignup(id, reviewNotes)
      : await apiClient.declineBetaSignup(id, reviewNotes)
    if (resp.success) {
      toast({ title: `Beta signup ${action}d` })
      loadSignups()
    }
    setReviewDialog(null)
    setReviewNotes('')
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Beta Signups</h1>
        <p className="text-sm text-muted-foreground">{total} {statusFilter} signups</p>
      </div>

      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="declined">Declined</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      ) : (
        <div className="app-surface rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b text-left text-xs font-medium text-muted-foreground">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">GitHub</th>
                <th className="px-4 py-3">Submitted</th>
                {statusFilter === 'pending' && <th className="px-4 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {signups.map((signup) => (
                <tr key={signup.id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-medium">{signup.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{signup.email}</td>
                  <td className="px-4 py-3">{signup.company || '-'}</td>
                  <td className="px-4 py-3 font-mono text-xs">{signup.githubUsername || '-'}</td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums">
                    {new Date(signup.submittedAt).toLocaleDateString()}
                  </td>
                  {statusFilter === 'pending' && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setReviewDialog({ id: signup.id, action: 'approve' })}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setReviewDialog({ id: signup.id, action: 'decline' })}
                        >
                          Decline
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {signups.length === 0 && (
                <tr>
                  <td colSpan={statusFilter === 'pending' ? 6 : 5} className="px-4 py-8 text-center text-muted-foreground">
                    No {statusFilter} signups
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!reviewDialog} onOpenChange={() => { setReviewDialog(null); setReviewNotes('') }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize">{reviewDialog?.action} Beta Signup</DialogTitle>
            <DialogDescription>Add optional notes for this decision.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Notes</Label>
            <Textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder="Optional notes..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReviewDialog(null); setReviewNotes('') }}>Cancel</Button>
            <Button
              variant={reviewDialog?.action === 'decline' ? 'destructive' : 'default'}
              onClick={handleReview}
              className="capitalize"
            >
              {reviewDialog?.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
